'use client';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useMemo } from 'react';
import { shopUrl } from '@/constants/environment';
import { routes } from '@/constants/routes';
import type { Route, RouteId } from '@/types';

export function useRoute() {
  const pathname = usePathname();
  const router = useRouter();

  const isRoute = useCallback(
    (routeId: RouteId): boolean => {
      const route = routes[routeId];
      if (!route) return false;
      return new RegExp(route.pattern).test(pathname);
    },
    [pathname],
  );

  const getRoute = useCallback((): Route | null => {
    const sortedRoutes = Object.entries(routes).sort(
      (a, b) => b[1].pattern.length - a[1].pattern.length,
    );
    for (const [, route] of sortedRoutes) {
      const regex = new RegExp(route.pattern);
      if (regex.test(pathname)) {
        return route;
      }
    }
    return null;
  }, [pathname]);

  const getRouteUrl = useCallback(
    (id: keyof typeof routes, params?: Record<string, string | number>): string => {
      const route = routes[id];
      if (!route) {
        throw new Error(`Route with id "${id}" not found.`);
      }
      let path = route.pathname;
      if (params) {
        for (const [key, value] of Object.entries(params)) {
          path = path.replace(`[${key}]`, encodeURIComponent(String(value)));
        }
      }
      if (/\[.*?\]/.test(path)) {
        throw new Error(`Missing params for route "${id}": ${path}`);
      }
      return path;
    },
    [],
  );

  const goToRoute = useCallback(
    (id: keyof typeof routes, params?: Record<string, string | number>): void => {
      router.push(getRouteUrl(id, params));
    },
    [router, getRouteUrl],
  );

  return {
    pathname,
    isRoute,
    getRoute,
    getRouteUrl,
    goToRoute,
  };
}

export function useUrl() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const currentUrl = useMemo(() => {
    const query = searchParams.toString();
    return query ? `${pathname}?${query}` : pathname;
  }, [pathname, searchParams]);
  const shopUrl = process.env.NEXT_PUBLIC_SHOP_URL;
  const loginUrl = `${shopUrl}/anmelden?redirect_url=${currentUrl}`;
  const registerUrl = `${shopUrl}/registrieren?redirect_url=${currentUrl}`;
  return {
    loginUrl,
    registerUrl,
  };
}

export const getAddProductToCartUrl = (productId: number): string =>
  `${shopUrl}/warenkorb/?add-to-cart=${productId}`;
